
import React, { useState, useEffect, useCallback } from 'react';
import { Mail, RefreshCw, HelpCircle, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmailTroubleshootingDialog } from './EmailTroubleshootingDialog';
import { useAuthContext } from '@/contexts/auth';
import { useHydrationSafe } from '@/hooks/useHydrationSafe';
import { supabase } from '@/lib/supabase/client';
import { toast } from 'sonner';

/**
 * Banner with warning/amber color scheme for unconfirmed email users.
 * Appears when a user is authenticated but hasn't confirmed their email.
 */
export function EmailConfirmationBanner() {
  const { isHydrated } = useHydrationSafe();
  const { isAuthenticated, user } = useAuthContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  // Cooldown timer
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => setCooldownSeconds(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  const email = user?.email;
  const isEmailVerified = !!user?.emailConfirmedAt;

  // Don't render during SSR, if not authenticated, if email is verified, or if dismissed
  if (!isHydrated || !isAuthenticated || isEmailVerified || dismissed || !email) {
    return null;
  }

  const handleResend = async () => {
    if (isResending || cooldownSeconds > 0 || !email) return;
    setIsResending(true);
    try {
      const { data, error } = await supabase.functions.invoke('post-signup-verify', {
        body: { userId: user.id, email }
      });
      if (error) {
        const msg = error.message || '';
        if (msg.includes('rate') || msg.includes('too many')) {
          toast.error('Troppe richieste. Attendi qualche minuto.');
          setCooldownSeconds(60);
        } else {
          toast.error('Errore durante l\'invio dell\'email.');
        }
      } else {
        toast.success('Email di conferma inviata!', {
          description: 'Controlla la tua casella email (anche spam).'
        });
        setCooldownSeconds(30);
      }
    } catch {
      toast.error('Errore durante l\'invio dell\'email');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <>
      <div 
        className="email-confirmation-banner fixed left-0 w-full bg-amber-500 text-white text-center text-xs font-medium px-4 py-2 z-[49] flex items-center justify-center gap-2 flex-wrap"
        style={{ top: '64px' }}
        role="alert"
        aria-live="polite"
      >
        <Mail className="w-4 h-4 flex-shrink-0" />
        <span className="hidden sm:inline">Conferma il tuo indirizzo email per sbloccare tutte le funzionalità.</span>
        <span className="sm:hidden">Conferma la tua email.</span>
        
        <div className="flex items-center gap-2 ml-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResend}
            disabled={isResending || cooldownSeconds > 0}
            className="h-6 px-2 text-xs bg-white/20 hover:bg-white/30 text-white border-0"
          >
            {isResending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : cooldownSeconds > 0 ? (
              `${cooldownSeconds}s`
            ) : (
              <>
                <RefreshCw className="w-3 h-3 mr-1" />
                <span className="hidden sm:inline">Reinvia</span>
              </>
            )}
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDialogOpen(true)}
            className="h-6 px-2 text-xs bg-white/20 hover:bg-white/30 text-white border-0"
          >
            <HelpCircle className="w-3 h-3 sm:mr-1" />
            <span className="hidden sm:inline">Aiuto</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDismissed(true)}
            className="h-6 w-6 p-0 text-xs bg-white/10 hover:bg-white/20 text-white border-0"
            aria-label="Nascondi banner"
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      </div>

      <EmailTroubleshootingDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        email={email}
        onResend={async () => { await handleResend(); return true; }}
        isResending={isResending}
        cooldownSeconds={cooldownSeconds}
      />
    </>
  );
}
